import { ArrowUpRight, Download, Expand } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from './ui/dialog';

const anatomy = [
  ['Body / iContainer', 'The embodiment of an identity.'],
  ['Brain / Chatbot', 'Reasons and plans.'],
  ['Heart / Governed execution', 'The core execution runtime turns permitted intent into action.'],
  ['Lungs / Shared application access point', 'Breathing is controlled exchange in and out, through the shared application socket to D-Bus operations.'],
  ['Spinal cord / FUSE filesystem', 'The body’s access pathway.'],
  ['Skeleton / Schema', 'Defines structure and constraints.'],
  ['Eyes / Evidence and visibility', 'Visibility that enables accountability.'],
  ['Hands and feet / End user', 'Human agency and interaction.'],
];

const diagrams = {
  regular: {
    label: 'Regular architecture',
    title: 'One system. Connected by design.',
    description: 'Identity, authority, policy, state, and evidence stay connected.',
    url: '/kit/assets/3tched-architecture-regular.webp',
    alt: 'Identity, authority, policy, state, and evidence connect above shared lungs, coordination and continuity, generated interfaces, and controlled transport. A complete text description follows.',
    sections: [
      ['Identity', 'Who is acting?'],
      ['Authority', 'What may they do?'],
      ['Policy', 'What rules apply?'],
      ['State', 'What changed?'],
      ['Evidence', 'What proves it? Evidence feeds retained context.'],
      ['Shared lungs', 'One shared application socket connects to D-Bus operations. Identity and permissions are checked before execution.'],
      ['Coordination and continuity', 'Models are selected for the task. Authorized agents execute. Decisions and context are retained.'],
      ['Generated interfaces', 'Typed schema leads to declared components and the interface, using Svelte / SvelteKit, Vite, and json-render.'],
    ],
  },
  embodied: {
    label: 'Embodied architecture',
    title: 'An identity, embodied.',
    description: 'One body. Connected by design.',
    url: '/kit/assets/3tched-architecture-embodied.webp',
    alt: 'The iContainer encloses the chatbot brain, execution heart, shared lungs, FUSE spinal cord, schema skeleton, evidence eyes, and end-user hands and feet. A complete text description follows.',
    sections: anatomy,
  },
};

export const ArchitectureDiagram = ({ variant }) => {
  const diagram = diagrams[variant];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className="text-link architecture-link" data-testid={`architecture-${variant}-trigger`}>
          {diagram.label} <Expand size={15} aria-hidden="true" />
        </button>
      </DialogTrigger>
      <DialogContent className="architecture-dialog" aria-modal="true" data-testid={`architecture-${variant}-dialog`}>
        <div className="architecture-dialog-heading">
          <DialogTitle className="architecture-dialog-title">{diagram.title}</DialogTitle>
          <DialogDescription className="architecture-dialog-description">{diagram.description} Open the full-size image to zoom into the details.</DialogDescription>
        </div>
        <img className="architecture-dialog-image" src={diagram.url} width="1536" height="1024" alt={diagram.alt} />
        <div className="architecture-diagram-actions">
          <a href={diagram.url} target="_blank" rel="noopener noreferrer" className="text-link">Open full-size image <ArrowUpRight size={15} aria-hidden="true" /></a>
          <a href={diagram.url} download className="text-link">Download diagram <Download size={15} aria-hidden="true" /></a>
        </div>
        <details className="architecture-description">
          <summary>Read the diagram description</summary>
          <dl>{diagram.sections.map(([part, description]) => <div key={part}><dt>{part}</dt><dd>{description}</dd></div>)}</dl>
          <p>Identity, authority, policy, state, and evidence stay connected.</p>
          <p>Ingress leads to the application door. Intended egress uses a private Xray socket and REALITY gateway; application binding remains pending. Transport protects the path; application policy governs the action.</p>
          <p>Architecture, not a deployment certificate. Components are verified separately; restoration and provisioning remain in progress.</p>
        </details>
      </DialogContent>
    </Dialog>
  );
};
